import {
  Box,
  Flex,
  Icon,
  Progress,
  Spinner,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import { RiCheckLine, RiCloseLine, RiErrorWarningLine } from "react-icons/ri";
import { UPLOAD_FILE_STATE } from "src/constants";
import { TextWithOverflowTooltip } from "../../TextWithOverflowTooltip";

interface UploadFileItemProps {
  file: File;
  progress: number;
  status: "uploading" | "success" | "error";
  errorMessage?: string;
  onRemoveFile: (file: File) => void;
}

export const UploadFileItem: React.FC<UploadFileItemProps> = ({
  file,
  progress,
  status,
  errorMessage,
  onRemoveFile,
}) => {
  const getProgressBarColor = () => {
    if (status === "error") return UPLOAD_FILE_STATE.error.colorScheme;
    if (status === "success") return UPLOAD_FILE_STATE.success.colorScheme;
    return UPLOAD_FILE_STATE.loading.colorScheme;
  };

  const getProgressBarText = () => {
    if (status === "error")
      return errorMessage || UPLOAD_FILE_STATE.error.messages.UPLOAD_ERROR;
    if (status === "success")
      return UPLOAD_FILE_STATE.success.messages.UPLOAD_SUCCESS;
    return UPLOAD_FILE_STATE.loading.messages.UPLOADING;
  };

  const shouldShowDismissButton = status === "error";

  return (
    <>
      <Box
        color="gray.600"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flex="1"
        minWidth="0"
      >
        {/* Styled as a pending (disabled) link until the upload finishes and the real link renders. */}
        <TextWithOverflowTooltip
          content={file.name}
          defaultTooltipContent={getProgressBarText()}
          hasArrow
          flex="1"
          minWidth="0"
          fontSize="md"
          color="primary.500"
          opacity={0.6}
          cursor="not-allowed"
        />
        {shouldShowDismissButton && (
          <Tooltip label="Dismiss" hasArrow>
            <span>
              <RiCloseLine
                cursor="pointer"
                onClick={() => onRemoveFile(file)}
              />
            </span>
          </Tooltip>
        )}
      </Box>
      {status === "error" && (
        <Text
          fontSize="xs"
          display="flex"
          alignItems="center"
          gap={0.5}
          color="red.600"
        >
          <RiErrorWarningLine /> {errorMessage || "Upload failed"}
        </Text>
      )}
      <Box mb={2}>
        {(status === "uploading" || status === "success") && (
          <>
            <Progress
              value={progress}
              borderRadius="300px"
              size="xs"
              colorScheme={getProgressBarColor()}
            />
            <Flex
              fontSize="xs"
              alignItems="center"
              gap={0.5}
              color={`${getProgressBarColor()}.600`}
            >
              {status === "uploading" ? (
                <Spinner size="xs" />
              ) : (
                <Icon as={RiCheckLine} />
              )}
              <Text>{getProgressBarText()}</Text>
            </Flex>
          </>
        )}
      </Box>
    </>
  );
};
